<template>
    <section class="dashboard-navigation-wrapper"
             :class="{
                 'hidden': isHidden,
                 'dashboard-navigation-wrapper--moved-down': isMovedDown
             }">
        <MobileHeader v-if="showMobileHeader" />
        <TabletHeader v-if="showTabletHeader"
                      @setMenuCardToggleRef="setMenuCardToggleRef" />
        <DesktopHeader v-if="showDesktopHeader" />
        <MenuCard v-if="navigationStore.showMenuCard"
                  ref="menuCardRef" />
    </section>
    <ShareTooltipMobile />
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import MobileHeader from './mobile/MobileHeader.vue';
    import DesktopHeader from './desktop/DesktopHeader.vue';
    import ShareTooltipMobile from '../../global/widgets/tooltips/ShareTooltipMobile.vue';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import { useAuthStore } from '@/js/store/auth/AuthStore';
    import { useNavigationStore } from '@/js/store/quests/dashboard/NavigationStore';
    import { useEventListener } from '@/js/composables/useEventListener';
    import TabletHeader from './tablet/TabletHeader.vue';
    import MenuCard from './menu/MenuCard.vue';
    import { useClickOutside } from '@/js/composables/useClickOutside';
    import { useCustomDeviceSize } from '@/js/composables/useCustomDeviceSize';

    defineProps<{ isMovedDown: boolean}>();

    const navigationStore = useNavigationStore();
    const authStore = useAuthStore();
    const { isMobileOrTablet } = useDeviceSize();
    const { isCustomLaptop, isCustomDesktop } = useCustomDeviceSize();
    const showMobileHeader = computed(() => isMobileOrTablet.value && authStore.isUserFetchCompleted);
    const showTabletHeader = computed(() => isCustomLaptop.value);
    const showDesktopHeader = computed(() => isCustomDesktop.value);
    const menuCardRef = ref(null);
    const menuCardToggleRef = ref<HTMLElement | null>(null);
    const isScrollingUp = ref(false);
    const lastScrollPosition = ref(0);
    const isHidden = computed(() => (showTabletHeader.value ? !isScrollingUp.value : false));

    const setMenuCardToggleRef = (ref: HTMLElement) => {
        menuCardToggleRef.value = ref;
    };

    const closeMenuCard = () => {
        navigationStore.showMenuCard = false;
    };

    useClickOutside(menuCardRef, closeMenuCard, [menuCardToggleRef]);

    const checkScrollDirection = () => {
        const scrollPosition = document.documentElement.scrollTop;

        if (scrollPosition > lastScrollPosition.value) {
            isScrollingUp.value = false;
            navigationStore.showMenuCard = false;
        } else {
            isScrollingUp.value = true;
        }

        lastScrollPosition.value = scrollPosition <= 0 ? 0 : scrollPosition;
    };

    useEventListener(window, 'scroll', checkScrollDirection);
</script>

<style scoped lang="scss">
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;
@use "@/sass/missions-partials/z-index" as missionsZIndex;

.dashboard-navigation-wrapper {
    position: relative;
    padding: 0 32px;
    margin-right: auto;
    margin-bottom: 48px;
    margin-left: auto;

    @media (max-width: 1024px) {
        position: sticky;
        z-index: missionsZIndex.$navigation-section-z-index;
        top: 0;
        padding: 0 16px;
        border-bottom: 1px solid legacyVariables.$dark-theme-border;
        margin-bottom: 32px;
        background-color: variables.$brand950;
        transition: top 0.3s ease-in-out;

        &.hidden {
            top: -82px;
        }
    }

    @media (max-width: breakpoints.$md-breakpoint-max-width) {
        z-index: missionsZIndex.$dashboard-navigation-wrapper-z-index;
        padding: 0;
    }

    &--moved-down {
        top: legacyVariables.$dashboard-bar-height;
    }
}
</style>
